/**
 * Invoices repository — the ONLY place touching the `invoices`, `invoice_items`,
 * `invoice_notifications`, and `scheduled_payments` tables.
 *
 * Strangler-Fig per-slice migration (manifest-6, Track A slice A4):
 *   Step 1 (done) — consolidated scattered `supabase.from("invoices" | …)` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/invoices/*`).
 *   Step 3 (HERE) — staff Finance-dashboard reads/writes flipped to the Nest
 *                   client; owner/tenant self-service invoice reads (which use
 *                   OTHER code paths, not this repo) stay on Supabase/RLS.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `list`, `getById`, `listStats`,
 *     `listOpen`, `listNotifications`, `createMasterInvoice` — all reached only
 *     by the staff Finance dashboard / master-invoices tab, whose callers hold
 *     `finance.{view,create}`. Each tries Nest first (only when an API is
 *     configured) and falls back to the original Supabase query on any error —
 *     a reversible unit; local dev without the Nest API keeps working. The Nest
 *     responses are parsed through the shared `@dallali/shared` schemas (a
 *     too-strict DTO would silently fall back to Supabase — the #1699 lesson).
 *     Write-path exception: `createMasterInvoice` and `verifyPaymentProof` fall
 *     back only on a known-uncommitted 4xx; ambiguous failures rethrow
 *     (duplicate-write guard). `payMasterInvoice`/`cancelMasterInvoice` keep the
 *     full fallback — their RPCs are idempotent server-side (reviewed 2026-07-18).
 *   KEPT on Supabase (documented):
 *     - `listByContract` (PaymentProgressBar in Contracts/UnitDetails) — reached
 *        by `contracts.view` / `units.view` staffers who may lack `finance.view`;
 *        a `finance`-gated endpoint would 403 them (the contracts-slice
 *        `listByUnit` precedent). Kept on Supabase until it gets a contracts/
 *        units-appropriate endpoint.
 *     - `pay_master_invoice` / `cancel_master_invoice` — atomic money RPCs not
 *        yet wrapped by a Nest endpoint (Track C1 financial-rpc-wrap); kept in PG.
 *     - all report / aggregate / dashboard-widget projections (listSummary,
 *        listFinancial, listMonthly, listTotals, listFor*, listUpcoming,
 *        listOutstandingBalances, listOverdueTenantIds, count, getMeta,
 *        getPreFill, listUnpaidByContract, listPaidByIds,
 *        listByScheduledPaymentIds, syncScheduledPayment,
 *        listPendingScheduledPayments, listMasterInvoices, listPaymentsStats) —
 *        cross-domain / no Nest endpoint yet; deferred backlog.
 *
 * FINANCIAL INVARIANT: the atomic money RPCs (`create_master_invoice`,
 * `list_open_invoices_for_bundling`, `pay_master_invoice`,
 * `cancel_master_invoice`) STAY in Postgres. When flipped, Nest forwards to the
 * SAME RPCs (no double-entry re-home). Amounts/totals/status from the Nest path
 * equal the Supabase path to the halala (asserted in the live smoke).
 *
 * Deferred to a follow-up (cross-domain reads — see PR body + matrix "read-by"):
 *   - `companiesWithInvoices` / `listForSuperAdmin` (super-admin Invoices
 *      company-tab page) — cross-company aggregations with no Nest endpoint yet;
 *      kept on raw Supabase (the existing `invoices` SELECT policy already
 *      permits the cross-company read, same path super-admin/Dashboard uses),
 *      mirroring the service_requests super-admin company-tabs slice. Flip when
 *      a super-admin-scoped Nest endpoint exists.
 *   - super-admin/Dashboard.tsx invoice reads are homed here: per-status COUNTS via
 *      `countByStatusForSuperAdmin`, and due/overdue amount SUMS via
 *      `sumTotalByStatusForSuperAdmin` (zero-row `sum_invoices_total_by_status` RPC).
 *   - src/hooks/useTenants.ts lines 235, 281 (tenant-level reads inside tenants slice)
 *   - src/hooks/useTenantData.ts line 146 (tenant portal — owner/tenant authz split)
 *   - src/hooks/useOwnerData.ts lines 394, 546 (owner portal — authz split)
 *   - src/pages/ContractDetails.tsx line 142 (cross-domain embedded read, contracts slice)
 *   - src/pages/finance/PaymentVerifications.tsx lines 223, 231 (verification-queue logic,
 *     also touches scheduled_payments; deferred to avoid scheduled_payments ownership conflict)
 *   - src/hooks/usePropertyAggregates.ts lines 24, 150, 380 (property-aggregates cross-domain)
 *   - src/hooks/useOwners.ts line 365 (owner aggregates cross-domain)
 *
 * See: docs/migration/domain-debugging-matrix.md (finance row)
 *      docs/handoff/open/migration-a4-invoices-core.md
 */
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { z } from "zod";
import {
  invoiceSchema,
  invoiceStatsRowSchema,
  openInvoiceRowSchema,
  invoiceNotificationRowSchema,
  invoiceSyncStateSchema,
  type InvoiceSyncState,
} from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Database } from "@/integrations/supabase/types";
import type {
  InvoiceWithRelations,
  InvoiceDetail,
  InvoiceNotificationRow,
  TenantFragment,
  OwnerFragment,
  RecipientFragment,
  ReceiptFragment,
} from "@/types/database";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";
import { companiesRepository } from "@/repositories/companies";


// Authenticated Nest request bound to the "invoices" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
// UNSCOPED: no x-active-role forwarded/required. Used by `listOpen` (GET
// /invoices/open — SupabaseAuthGuard + PermissionGuard only, no RowScopeGuard)
// and `createMasterInvoice` (POST /invoices/master — a write; WRITE endpoints
// never carry RowScopeGuard, capability-gated only). Neither must ever be
// blocked or rerouted to the Supabase fallback by a missing/unresolved active
// role (issue #3278 — a client-side guard on a shared instance previously did
// exactly that).
const nestRequest = makeNestRequest("invoices");

// Row-scoped variant: `list`, `getById`, `listStats`, `listNotifications` hit
// endpoints that carry RowScopeGuard and require x-active-role (ADR-0001) —
// forwarded from sessionStorage["dallali-active-role"] on every call;
// `requireActiveRole` short-circuits the boot-race window client-side.
const nestRequestScoped = makeNestRequest("invoices", {
  forwardActiveRole: true,
  requireActiveRole: true,
});

// ─── Master-invoice data contract (owned by this repository) ─────

export type MasterInvoiceRow =
  Database["public"]["Tables"]["master_invoices"]["Row"];
export type MasterInvoiceStatus =
  Database["public"]["Enums"]["master_invoice_status"];

/** A child invoice row as surfaced inside a master invoice's line items. */
export interface MasterInvoiceChild {
  id: string;
  invoice_number: string | null;
  /** The child invoice's own description (e.g. "Property Management Fee (Owner)"),
   *  shown as the master line item's primary label instead of a raw id fragment. */
  description: string | null;
  subtotal: number;
  vat_rate: number | null;
  vat_amount: number;
  discount_amount: number;
  total_amount: number;
  balance: number;
  status: Database["public"]["Enums"]["invoice_status"];
  due_date: string;
  display_order: number;
  /** Property + unit + tenant the child invoice belongs to, so each master-invoice
   *  line item can show which property/unit/tenant it is for (matches the legacy
   *  invoice format the team asked to restore). Null when the invoice has no
   *  linked property/unit, or no contract/tenant. */
  property_name: string | null;
  unit_number: string | null;
  tenant_name: string | null;
}

/** The three kinds of billed party a master/invoice can resolve to. Mirrors the
 *  `party_kind` returned by the `invoice_billed_party` SQL resolver — keep in sync. */
export type PartyKind = "owner" | "tenant" | "profile";

export interface MasterInvoiceWithItems extends MasterInvoiceRow {
  recipient: { full_name: string | null; phone: string | null; email: string | null } | null;
  /** Persisted billed party (so the client name shows on list/detail/print even
   *  when the party has no platform user). Set by create_master_invoice. */
  client_party_kind: PartyKind | null;
  client_party_id: string | null;
  client_name: string | null;
  items: MasterInvoiceChild[];
}

// ─── Shared option types ──────────────────────────────────────────

export interface ScopeOptions {
  companyId: string;
  /** true (default) = always filter by company_id. false = super-admin bypass. */
  scopeToCompany?: boolean;
}

export interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

export interface AssignedPropertyFilter {
  /** Non-null array = employee scoped to these properties; null = no restriction. */
  assignedPropertyIds: string[] | null;
}

// ─── Light projection types used by aggregates / stats ───────────

export interface InvoiceStatsRow {
  total_amount: number | null;
  balance: number | null;
  status: string | null;
}

export interface InvoiceSummaryRow {
  id: string;
  contract_id: string | null;
  created_at: string;
  total_amount: number | null;
  paid_amount: number | null;
  description: string | null;
}

export interface InvoiceFinancialRow {
  total_amount: number | null;
  balance: number | null;
  status: string | null;
  invoice_date: string | null;
  paid_amount: number | null;
}

export interface InvoicePaymentsStatRow {
  id: string;
  status: string | null;
  due_date: string;
  balance: number | null;
  paid_amount: number | null;
  total_amount: number | null;
}

export interface InvoiceMonthlyRow {
  total_amount: number | null;
  invoice_date: string | null;
}

export interface InvoiceIncomeStatementRow {
  total_amount: number | null;
  description: string | null;
  contract: { contract_type: string | null } | null;
}

export interface InvoiceOwnerPayoutsRow {
  id: string;
  property_id: string | null;
  total_amount: number | null;
  description: string | null;
  contract: { contract_type: string | null } | null;
}

export interface InvoicePropertyPerformanceRow {
  property_id: string | null;
  paid_amount: number | null;
}

export interface InvoiceTotalsRow {
  total_amount: number | null;
  created_at: string;
}

export interface InvoicePendingVerificationRow {
  id: string;
}

/**
 * An open invoice + its resolved BILLED PARTY (the tenant/owner record, or a
 * recipient profile). The party — not recipient_id — is the master-invoice
 * grouping key, because most billed tenants have no platform user account.
 *
 * party_id/party_name are NULL when the billed party can't be resolved (no owner
 * on the contract/property/unit). Such rows are RETURNED by
 * `list_open_invoices_for_bundling` (no longer hidden) so the create dialog can
 * surface them as "needs owner — fix the contract/property"; they are NOT
 * bundleable. `party_kind` is always present — invoice_billed_party classifies a
 * kind even when the id can't be resolved (e.g. ('owner', NULL)).
 */
export interface OpenInvoiceRow {
  id: string;
  invoice_number: string | null;
  total_amount: number | null;
  balance: number | null;
  party_kind: PartyKind;
  party_id: string | null;
  party_name: string | null;
  /** Coarse invoice category derived from the description (rent / fee / other). */
  type_key: "rent" | "fee" | "other";
  /** Property + unit names so the bundling picker shows which property/unit each
   *  open invoice belongs to (CEO request). Null when unresolved. */
  property_name: string | null;
  unit_number: string | null;
}

export interface InvoiceMetaRow {
  id: string;
  company_id: string;
  contract_id: string | null;
  tenant_id: string | null;
  property_id: string | null;
  unit_id: string | null;
  scheduled_payment_id: string | null;
}

export interface InvoicePreFillRow {
  id: string;
  invoice_number: string | null;
  due_date: string;
  total_amount: number | null;
  balance: number | null;
  status: string | null;
  description: string | null;
  property_id: string | null;
  unit_id: string | null;
  contract_id: string | null;
  contract: {
    id: string;
    contract_number: string | null;
    contract_type: string | null;
    tenant: { first_name: string | null; last_name: string | null; commercial_name: string | null } | null;
  } | null;
  property: { id: string; name: string; name_en: string | null; deed_number: string | null } | null;
  unit: { id: string; unit_number: string | null; unit_type: string | null } | null;
  /** expense_type of the expense this invoice was generated from
   *  (generate_invoice_from_expense); null for non-expense invoices. Drives
   *  the payment dialog's default transaction type. */
  source_expense_type: Database["public"]["Enums"]["expense_type"] | null;
}

export interface UnpaidInvoiceRow {
  id: string;
  invoice_number: string | null;
  due_date: string;
  total_amount: number | null;
  balance: number | null;
  status: string | null;
}

/** Subscription invoice row (M2.6: invoices with type='subscription'). */
export interface SubscriptionInvoiceRow {
  id: string;
  invoice_number: string | null;
  total_amount: number;
  subtotal: number | null;
  vat_amount: number | null;
  vat_rate: number | null;
  status: string;
  invoice_date: string;
  due_date: string;
  updated_at: string;
  notes: string | null;
  created_at: string;
  subscription_id: string;
}

export interface ScheduledPaymentSyncData {
  scheduledPaymentId: string;
  status: "paid" | "partial";
  paidAmount: number;
}

export interface CreateMasterInvoiceParams {
  companyId: string;
  invoiceIds: string[];
  recipientId: string | null;
  invoiceType?: string | null;
  notes?: string | null;
}

export interface PayMasterInvoiceParams {
  masterInvoiceId: string;
  paymentChannel?: string;
  referenceNumber?: string | null;
}

/** Params for `verifyPaymentProof` — approve/reject a bank-transfer proof. */
export interface VerifyPaymentProofParams {
  paymentId: string;
  action: "approve" | "reject";
  /** Null lets the RPC derive the linked invoice's full outstanding balance. */
  paidAmount?: number | null;
  reason?: string | null;
}

/** Params for `submitPaymentProof` — a tenant submits their bank-transfer proof. */
export interface SubmitPaymentProofParams {
  paymentId: string;
  /** Storage path of the already-uploaded proof file. */
  proofUrl: string;
  method?: "bank_transfer";
  referenceNumber?: string | null;
}

/** Result of cancel_master_invoice — `released` = number of child links removed. */
export interface CancelMasterInvoiceResult {
  status: string;
  released: number;
  idempotent: boolean;
}

/** Result of delete_master_invoice — a hard delete, so there is no `idempotent` flag. */
export interface DeleteMasterInvoiceResult {
  master_invoice_id: string;
  master_invoice_number: string | null;
  deleted: true;
}

export interface ScheduledPaymentPendingRow {
  id: string;
}

export interface InvoiceScheduledPaymentRow {
  id: string;
}

/**
 * A scheduled payment carrying a transfer proof, joined with its contract →
 * tenant/unit/property display columns — one row of the PaymentVerifications
 * review table.
 */
export interface ScheduledPaymentProofRow {
  id: string;
  contract_id: string;
  company_id: string;
  payment_number: number;
  due_date: string;
  amount: number;
  paid_amount: number | null;
  status: string;
  transfer_proof_url: string | null;
  transfer_method: string | null;
  transfer_reference_number: string | null;
  transfer_proof_uploaded_at: string | null;
  verification_status: "pending" | "rejected" | "approved" | null;
  verified_at: string | null;
  rejection_reason: string | null;
  contract: {
    contract_number: string | null;
    tenant: {
      first_name: string | null;
      last_name: string | null;
      first_name_en: string | null;
      last_name_en: string | null;
      commercial_name: string | null;
      commercial_name_en: string | null;
    } | null;
    unit: { unit_number: string | null } | null;
    property: { name: string | null } | null;
  } | null;
}

/**
 * The invoice payment columns the verification-approve dialog needs to
 * pre-compute the matched total/subtotal/VAT/balance.
 */
export interface InvoiceVerificationMatchRow {
  id: string;
  total_amount: number;
  paid_amount: number | null;
  subtotal: number | null;
  vat_amount: number | null;
  status: string;
  due_date: string | null;
  created_at: string;
}

/**
 * One company tab in the super-admin Invoices view (mirrors
 * `ServiceRequestCompany`): the company + its invoice count, with `is_main`
 * driving main-first ordering. `total` is the invoice count, not an amount.
 */
export interface InvoiceCompany {
  company_id: string;
  name: string;
  name_en: string | null;
  total: number;
  is_main: boolean;
}

/**
 * A flat invoice row for the super-admin cross-company list. Only the columns
 * the ops-finance table renders. `client_name` is resolved from the billed
 * party (recipient profile → contract tenant → contract owner), because
 * `recipient_id` is null on almost all invoices — the billed party lives on the
 * contract (the master-invoice billed-party precedent).
 */
export interface SuperAdminInvoiceRow {
  id: string;
  invoice_number: string | null;
  company_id: string;
  total_amount: number | null;
  balance: number | null;
  paid_amount: number | null;
  status: string | null;
  invoice_date: string | null;
  due_date: string;
  description: string | null;
  client_name: string | null;
  property_name: string | null;
  unit_number: string | null;
}

// ─── Supabase fallback impls (live path for kept methods; fallback for flipped) ─

async function listViaSupabase(
  { companyId, assignedPropertyIds }: ScopeOptions & AssignedPropertyFilter,
): Promise<InvoiceWithRelations[]> {
  let query = supabase
    .from("invoices")
    .select(`
      *,
      recipient:profiles!invoices_recipient_id_fkey(full_name, phone, email),
      property:properties(name),
      unit:units(unit_number),
      contract:contracts(contract_number, owner_id, contract_type)
    `)
    .eq("company_id", companyId)
    .is("deleted_at", null);

  if (assignedPropertyIds && assignedPropertyIds.length > 0) {
    query = query.in("property_id", assignedPropertyIds);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching invoices:", serializeError(error));
    throw error;
  }

  return (data ?? []) as unknown as InvoiceWithRelations[];
}

// ─── Repository ───────────────────────────────────────────────────

export const invoicesRepository = {
  /**
   * Canonical invoices list with cross-schema relations, newest first.
   * Company-scoped + soft-deleted excluded. Employee scope applied when
   * assignedPropertyIds is a non-empty array.
   *
   * Staff op — flipped to Nest (company resolved server-side from the JWT),
   * Supabase fallback. The Nest endpoint is company-wide, so employee-scoped
   * lists (non-empty assignedPropertyIds) stay on Supabase to preserve the
   * assigned-property filter (the contracts-slice precedent).
   */
  async list(
    params: ScopeOptions & AssignedPropertyFilter,
  ): Promise<InvoiceWithRelations[]> {
    const { assignedPropertyIds } = params;
    const isEmployeeScoped =
      assignedPropertyIds !== null && assignedPropertyIds.length > 0;
    if (API_URL && !isEmployeeScoped) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.invoices.list());
        return z.array(invoiceSchema).parse(rows) as unknown as InvoiceWithRelations[];
      } catch (err) {
        reportNestFallback("invoices.list", err);
      }
    }
    return listViaSupabase(params);
  },

  /**
   * Fetch a single invoice with full relations for the detail view.
   * Cross-references tenants, owners, receipts, and profiles for recipient.
   *
   * Staff op — flipped to Nest (which replicates the same enrichment
   * server-side), Supabase fallback. The Nest response is parsed through the
   * shared `invoiceSchema` (loose relations + tolerant money fields). A 404
   * from Nest (invoice not found / cross-tenant, 404-not-403) is the
   * contract's own "not found → null" outcome, NOT a degradation: it returns
   * null directly — no Sentry fallback event (#3362 was pure 404 noise), no
   * redundant Supabase round-trip that would re-derive the same null.
   */
  async getById(id: string, companyId: string): Promise<InvoiceDetail | null> {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(API_ROUTES.invoices.getById(id));
        return (row ? invoiceSchema.parse(row) : null) as unknown as InvoiceDetail | null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) {
          return null;
        }
        reportNestFallback("invoices.getById", err);
      }
    }
    return this.getByIdViaSupabase(id, companyId);
  },

  /**
   * Fetch a single invoice with full relations for the detail view (Supabase
   * impl — the fallback for `getById` and the live path when no Nest API).
   * Cross-references tenants, owners, receipts, and profiles for recipient.
   */
  async getByIdViaSupabase(id: string, companyId: string): Promise<InvoiceDetail | null> {
    const { data: invoice, error } = await supabase
      .from("invoices")
      .select(`
        *,
        property:properties(id, name, address),
        unit:units(id, unit_number),
        contract:contracts(id, contract_type, contract_number, start_date, owner_id, tenant_id),
        items:invoice_items(*)
      `)
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching invoice:", serializeError(error));
      throw error;
    }

    if (!invoice) return null;

    // Fetch tenant data from tenants table via contract
    let tenant: TenantFragment | null = null;
    const contractData = invoice.contract as unknown as {
      tenant_id?: string | null;
      owner_id?: string | null;
    } | null;
    if (contractData?.tenant_id) {
      const { data: tenantData } = await supabase
        .from("tenants")
        .select(
          "first_name, last_name, first_name_en, last_name_en, commercial_name, commercial_name_en, tenant_type, phone, email, address, cr_number, vat_number, id_number, id_type",
        )
        .eq("id", contractData.tenant_id)
        .eq("company_id", companyId)
        .maybeSingle();
      tenant = tenantData as TenantFragment | null;
    }

    // Fetch owner data if contract has owner_id
    let owner: OwnerFragment | null = null;
    if (contractData?.owner_id) {
      const { data: ownerData } = await supabase
        .from("owners")
        .select("full_name, full_name_en, phone, email, address, national_id, owner_type, commercial_name, commercial_name_en, representative_name, representative_name_en, cr_number, unified_number, vat_number")
        .eq("id", contractData.owner_id)
        .eq("company_id", companyId)
        .maybeSingle();
      owner = ownerData as OwnerFragment | null;
    }

    // Multi-ownership fallback: if contract has no owner, check unit.owner_id
    if (!owner && invoice.unit_id) {
      const { data: unitData } = await supabase
        .from("units")
        .select("owner_id")
        .eq("id", invoice.unit_id)
        .eq("company_id", companyId)
        .maybeSingle();
      if (unitData?.owner_id) {
        const { data: ownerData } = await supabase
          .from("owners")
          .select("full_name, full_name_en, phone, email, address, national_id, owner_type, commercial_name, commercial_name_en, representative_name, representative_name_en, cr_number, unified_number, vat_number")
          .eq("id", unitData.owner_id)
          .eq("company_id", companyId)
          .maybeSingle();
        owner = ownerData as OwnerFragment | null;
      }
    }

    // Multi-ownership fallback #2: the owner lives on the PROPERTY (the canonical
    // model — Company → Properties.owner_id). Expense invoices billed to the owner
    // commonly have no contract-owner and no unit-owner, so without this the owner
    // record never resolves and recipient_role='owner' renders blank / mis-bills to
    // the tenant (ticket 86exwj1jg).
    if (!owner && invoice.property_id) {
      const { data: propData } = await supabase
        .from("properties")
        .select("owner_id")
        .eq("id", invoice.property_id)
        .eq("company_id", companyId)
        .maybeSingle();
      if (propData?.owner_id) {
        const { data: ownerData } = await supabase
          .from("owners")
          .select("full_name, full_name_en, phone, email, address, national_id, owner_type, commercial_name, commercial_name_en, representative_name, representative_name_en, cr_number, unified_number, vat_number")
          .eq("id", propData.owner_id)
          .eq("company_id", companyId)
          .maybeSingle();
        owner = ownerData as OwnerFragment | null;
      }
    }

    // Fetch receipts linked to this invoice
    const { data: receipts } = await supabase
      .from("receipts")
      .select("id, receipt_number, payment_date, amount, payment_channel")
      .eq("invoice_id", id)
      .is("deleted_at", null)
      .order("payment_date", { ascending: false });

    // Fetch recipient profile if invoice has recipient_id (fee invoices post-PR #956)
    let recipient: RecipientFragment | null = null;
    const invoiceAny = invoice as unknown as Record<string, unknown>;
    const recipientId = invoiceAny["recipient_id"] as string | null | undefined;
    if (recipientId) {
      const { data: recipientData } = await supabase
        .from("profiles")
        .select("full_name, full_name_en")
        .eq("id", recipientId)
        .maybeSingle();
      recipient = recipientData;
    }

    return {
      ...invoice,
      tenant,
      owner,
      receipts: (receipts ?? []) as ReceiptFragment[],
      recipient,
      recipient_id: recipientId ?? null,
    } as unknown as InvoiceDetail;
  },

  /**
   * Fetch invoice metadata needed by AddPaymentDialog before calling record_payment.
   * Returns only the fields the payment flow needs. Company-scoped.
   */
  async getMeta(id: string, companyId: string): Promise<InvoiceMetaRow | null> {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, company_id, contract_id, tenant_id, property_id, unit_id, scheduled_payment_id")
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching invoice meta:", serializeError(error));
      throw error;
    }

    return data as InvoiceMetaRow | null;
  },

  /**
   * Fetch a pre-fill projection for AddPaymentDialog (context mode: invoice
   * already known). Returns the compact fields the payment form needs.
   */
  async getPreFill(id: string, companyId: string): Promise<InvoicePreFillRow | null> {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id,
        invoice_number,
        due_date,
        total_amount,
        balance,
        status,
        description,
        property_id,
        unit_id,
        contract_id,
        contract:contracts(
          id,
          contract_number,
          contract_type,
          tenant:tenants(first_name, last_name, commercial_name)
        ),
        property:properties(id, name, name_en, deed_number),
        unit:units(id, unit_number, unit_type),
        source_expense:expenses!expenses_generated_invoice_id_fkey(expense_type)
      `)
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching invoice prefill:", serializeError(error));
      throw error;
    }

    if (!data) return null;

    // The reverse embed comes back as an array (at most one row — the expense
    // that generated this invoice). Flatten to a single nullable field.
    const { source_expense, ...row } = data as unknown as InvoicePreFillRow & {
      source_expense?: { expense_type: Database["public"]["Enums"]["expense_type"] | null }[] | null;
    };
    return { ...row, source_expense_type: source_expense?.[0]?.expense_type ?? null };
  },

  /**
   * List all invoices for a contract (any status), ordered by due date.
   * Used by PaymentProgressBar to compute payment progress.
   * company_id filter enforces multi-tenant isolation — a contract_id from
   * another company must never leak invoice data.
   */
  async listByContract(contractId: string, companyId: string): Promise<{ id: string; status: string; total_amount: number; paid_amount: number | null }[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, status, total_amount, paid_amount")
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("due_date", { ascending: true });

    if (error) {
      console.error("Error fetching invoices by contract:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { id: string; status: string; total_amount: number; paid_amount: number | null }[];
  },

  /**
   * Tenant-portal invoices for a single contract (used by useTenantInvoices).
   * Unit/property relations for display, due_date ascending. Optional `range`
   * bounds `created_at` (month/year time filter, CEO ask — the Total
   * Contracts/Due Payments/Total Services stat-card convention: "created
   * within the selected month", matching the admin dashboard's
   * usePropertiesStats/useContractsStats precedent).
   */
  async listForTenantPortal(
    contractId: string,
    range?: { startDate?: string; endDate?: string },
  ) {
    let query = supabase
      .from("invoices")
      .select(`
        *,
        unit:units(id, unit_number),
        property:properties(id, name)
      `)
      .eq("contract_id", contractId);

    if (range?.startDate) query = query.gte("created_at", range.startDate);
    if (range?.endDate) query = query.lte("created_at", range.endDate);

    const { data, error } = await query.order("due_date", { ascending: true });

    if (error) {
      console.error("Error fetching tenant invoices:", serializeError(error));
      throw error;
    }

    return data ?? [];
  },

  /**
   * Batch-fetch payment-progress invoices for a set of contract IDs.
   * Returns only the four columns PaymentProgressBar needs.
   * company_id filter enforces multi-tenant isolation.
   * Returns [] immediately when contractIds is empty (avoids a needless round-trip).
   *
   * IDs are CHUNKED (100/request): PostgREST `.in()` puts every uuid in the
   * query string, and 300+ contracts (~11KB URL) exceeds URL limits — the
   * request dies at fetch level ("Failed to fetch", qa-daily run 28648665457).
   */
  async listByContracts(
    contractIds: string[],
    companyId: string,
  ): Promise<{ id: string; contract_id: string; status: string; total_amount: number; paid_amount: number | null }[]> {
    if (contractIds.length === 0) return [];

    const CHUNK = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < contractIds.length; i += CHUNK) {
      chunks.push(contractIds.slice(i, i + CHUNK));
    }

    const results = await Promise.all(
      chunks.map(async (ids) => {
        const { data, error } = await supabase
          .from("invoices")
          .select("id, contract_id, status, total_amount, paid_amount")
          .in("contract_id", ids)
          .eq("company_id", companyId)
          .is("deleted_at", null)
          .order("due_date", { ascending: true });

        if (error) {
          console.error("Error fetching invoices by contracts:", serializeError(error));
          throw error;
        }
        return (data ?? []) as { id: string; contract_id: string; status: string; total_amount: number; paid_amount: number | null }[];
      }),
    );

    return results.flat();
  },

  /**
   * List a company's subscription invoices (Fortress M2.6 consolidation:
   * subscription invoices are rows in `invoices` with type='subscription' +
   * subscription_id; the legacy subscription_invoices table is frozen).
   *
   * `subscription_id` isn't in the generated supabase types yet — regenerate
   * src/integrations/supabase/types.ts after the migration lands on staging
   * (follow-up); `.filter()` + `.returns<>()` bridge until then (same pattern
   * as BrokeragePropertyResolver's rer_number cast-bridge).
   */
  async listBySubscription(subscriptionId: string, companyId: string): Promise<SubscriptionInvoiceRow[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select(
        "id, invoice_number, total_amount, subtotal, vat_amount, vat_rate, status, invoice_date, due_date, updated_at, notes, created_at, subscription_id" as "*",
      )
      .eq("company_id", companyId)
      .filter("subscription_id", "eq", subscriptionId)
      .is("deleted_at", null)
      .order("invoice_date", { ascending: false })
      .returns<SubscriptionInvoiceRow[]>();

    if (error) {
      console.error("Error fetching subscription invoices:", serializeError(error));
      throw error;
    }

    return data ?? [];
  },

  /**
   * List a contract's scheduled-payment rows (payment_number, due_date, amount),
   * ordered by payment_number. `amount` is the pure EX-VAT rent per payment —
   * used by EditContractDrawer to re-hydrate a flexible/custom payment table on
   * edit so the user doesn't have to recreate it by hand.
   */
  async listScheduleByContract(
    contractId: string,
    companyId: string,
  ): Promise<{ payment_number: number; due_date: string; amount: number }[]> {
    const { data, error } = await supabase
      .from("scheduled_payments")
      .select("payment_number, due_date, amount")
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .order("payment_number", { ascending: true });

    if (error) {
      console.error("Error fetching scheduled payments by contract:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { payment_number: number; due_date: string; amount: number }[];
  },

  /**
   * List unpaid invoices for a contract, ordered by due date.
   * Used by AddPaymentDialog to populate the invoice selector.
   */
  async listUnpaidByContract(contractId: string, companyId: string): Promise<UnpaidInvoiceRow[]> {
    // Every payable status: tenant-responsibility expense invoices (born
    // 'due' WITH a contract attached by generate_invoice_from_expense) and
    // rent invoices that aged into 'overdue' were both invisible to the old
    // issued/partial filter, making them unrecordable in manual mode.
    // (Owner/company-responsibility expense invoices have no contract and
    // never appear here — they are paid via context mode instead.)
    const { data, error } = await supabase
      .from("invoices")
      .select("id, invoice_number, due_date, total_amount, balance, status")
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .in("status", ["issued", "due", "overdue", "partial"])
      .order("due_date");

    if (error) {
      console.error("Error fetching unpaid invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as UnpaidInvoiceRow[];
  },

  /**
   * Sync a scheduled payment's status + paid_amount after record_payment.
   * Called by AddPaymentDialog after the RPC succeeds.
   * Company-scoped via the scheduled_payment_id (already validated by RPC).
   */
  async syncScheduledPayment({ scheduledPaymentId, status, paidAmount }: ScheduledPaymentSyncData): Promise<void> {
    const { error } = await supabase
      .from("scheduled_payments")
      .update({ status, paid_amount: paidAmount })
      .eq("id", scheduledPaymentId);

    if (error) {
      console.error("Error syncing scheduled payment:", serializeError(error));
      throw error;
    }
  },

  /**
   * Count invoices for a company (for dashboard stats). Returns just the count.
   */
  async count(companyId: string): Promise<number> {
    const { count, error } = await supabase
      .from("invoices")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error counting invoices:", serializeError(error));
      throw error;
    }

    return count ?? 0;
  },

  /**
   * Payment status stats — status / due_date / amounts for the dashboard
   * payments widget (usePaymentsStats).
   *
   * @deprecated Use `paymentsStatsCounts` instead — it uses server-side
   * `count: 'exact', head: true` bucket queries and returns O(1) integers
   * rather than fetching O(N) full rows with money columns. Kept for callers
   * that need the raw rows for non-count purposes.
   */
  async listPaymentsStats(companyId: string): Promise<InvoicePaymentsStatRow[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, status, due_date, balance, paid_amount, total_amount")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching payments stats:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoicePaymentsStatRow[];
  },

  /**
   * Dashboard payments-widget stat counts — three server-side bucket queries
   * (paid, overdue, due) that each return only a `Content-Range` header count
   * (zero row body). Replaces the old `listPaymentsStats` full-row fetch for
   * the dashboard use-case: payload drops from O(N rows × 6 money columns) to
   * O(1) integers per bucket.
   *
   * Bucket definitions (mirror the JS-side logic in usePaymentsStats):
   *   paid    — status IN ('paid', 'overpaid')
   *   overdue — status NOT IN ('paid', 'overpaid') AND due_date < today
   *   due     — status NOT IN ('paid', 'overpaid') AND due_date >= today
   */
  async paymentsStatsCounts(companyId: string, today: string): Promise<{
    total: number;
    paid: number;
    overdue: number;
    due: number;
  }> {
    const baseQuery = () =>
      supabase
        .from("invoices")
        .select("*", { count: "exact", head: true })
        .eq("company_id", companyId)
        .is("deleted_at", null);

    const [totalRes, paidRes, overdueRes, dueRes] = await Promise.all([
      baseQuery(),
      // paid bucket: status is 'paid' or 'overpaid'
      baseQuery().in("status", ["paid", "overpaid"]),
      // overdue bucket: not paid, past due date
      baseQuery().not("status", "in", "(paid,overpaid)").lt("due_date", today),
      // due bucket: not paid, due today or later
      baseQuery().not("status", "in", "(paid,overpaid)").gte("due_date", today),
    ]);

    for (const { error } of [totalRes, paidRes, overdueRes, dueRes]) {
      if (error) {
        console.error("Error fetching payments stats counts:", serializeError(error));
        throw error;
      }
    }

    return {
      total: totalRes.count ?? 0,
      paid: paidRes.count ?? 0,
      overdue: overdueRes.count ?? 0,
      due: dueRes.count ?? 0,
    };
  },

  /**
   * Finance stats projection — total_amount / balance / status.
   * Used by useFinancialStats to compute pending amounts.
   *
   * Staff op — flipped to Nest (`/invoices/stats`), Supabase fallback. The Nest
   * response is the same minimal projection, parsed through the shared
   * `invoiceStatsRowSchema` (tolerant money fields). The parsed rows are cast
   * back to the Supabase-inferred `InvoiceStatsRow` so consumers see no drift.
   */
  async listStats(companyId: string): Promise<InvoiceStatsRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.invoices.stats());
        return invoiceStatsRowSchema.array().parse(rows) as unknown as InvoiceStatsRow[];
      } catch (err) {
        reportNestFallback("invoices.listStats", err);
      }
    }
    const { data, error } = await supabase
      .from("invoices")
      .select("total_amount, balance, status")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching invoice stats:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceStatsRow[];
  },

  /**
   * Finance summary projection — id / contract_id / created_at / total_amount /
   * paid_amount / description. Used by useFinanceSummaryStats to classify
   * invoices by kind (rent / fee / sale).
   * Optionally date-filtered (gte/lte created_at).
   */
  async listSummary(companyId: string, filter?: DateRangeFilter): Promise<InvoiceSummaryRow[]> {
    let query = supabase
      .from("invoices")
      .select("id, contract_id, created_at, total_amount, paid_amount, description")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("created_at", filter.startDate);
    if (filter?.endDate) query = query.lte("created_at", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching invoice summary:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceSummaryRow[];
  },

  /**
   * Filtered financial stats projection (invoice_date-filtered).
   * Used by useFilteredFinancialStats and useReportsData.
   */
  async listFinancial(companyId: string, filter?: DateRangeFilter): Promise<InvoiceFinancialRow[]> {
    let query = supabase
      .from("invoices")
      .select("total_amount, balance, status, invoice_date, paid_amount")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("invoice_date", filter.startDate);
    if (filter?.endDate) query = query.lte("invoice_date", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching invoice financial data:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceFinancialRow[];
  },

  /**
   * Monthly chart projection (invoice_date filtered). Used by useMonthlyFinancialData.
   */
  async listMonthly(companyId: string, sinceDate: string): Promise<InvoiceMonthlyRow[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("total_amount, invoice_date")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .gte("invoice_date", sinceDate);

    if (error) {
      console.error("Error fetching monthly invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceMonthlyRow[];
  },

  /**
   * Income-statement projection. Used by IncomeStatementReport.
   */
  async listForIncomeStatement(companyId: string): Promise<InvoiceIncomeStatementRow[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("total_amount, description, contract:contracts(contract_type)")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching income statement invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as InvoiceIncomeStatementRow[];
  },

  /**
   * Owner-payout revenue/fee classification projection — property-scoped
   * invoices with enough shape (contract_type via embed + description) to
   * run through classifyInvoice. Used by useOwnerPayouts to tell the
   * owner's own rent/sale money apart from Dallali's own fee invoices
   * (management/brokerage/marketing/etc.) billed against the same property
   * (#CEO 2026-07-23 finding — the payouts revenue column stacked a sale
   * receipt + its brokerage-fee receipt into one number).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice (mirrors
   * the receipts/expenses siblings for this same read).
   */
  async listForOwnerPayouts(
    companyId: string,
    propertyIds: string[],
  ): Promise<InvoiceOwnerPayoutsRow[]> {
    if (propertyIds.length === 0) return [];

    const { data, error } = await supabase
      .from("invoices")
      .select("id, property_id, total_amount, description, contract:contracts(contract_type)")
      .eq("company_id", companyId)
      .in("property_id", propertyIds)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching invoices for owner payouts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as InvoiceOwnerPayoutsRow[];
  },

  /**
   * Balance-sheet projection — balance / status. Used by BalanceSheetReport.
   */
  async listForBalanceSheet(companyId: string): Promise<{ balance: number | null; status: string | null }[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("balance, status")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching balance sheet invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { balance: number | null; status: string | null }[];
  },

  /**
   * Property performance projection — property_id / paid_amount.
   * Used by PropertyPerformanceReport.
   */
  async listForPropertyPerformance(companyId: string): Promise<InvoicePropertyPerformanceRow[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("property_id, paid_amount")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching property performance invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoicePropertyPerformanceRow[];
  },

  /**
   * Totals projection (created_at-filtered). Used by TotalsSummaryCards.
   */
  async listTotals(companyId: string, filter?: DateRangeFilter): Promise<InvoiceTotalsRow[]> {
    let query = supabase
      .from("invoices")
      .select("total_amount, created_at")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("created_at", filter.startDate);
    if (filter?.endDate) query = query.lte("created_at", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching invoice totals:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceTotalsRow[];
  },

  /**
   * Open invoices for master-invoice bundling (MasterInvoicesTab), each with its
   * resolved BILLED PARTY (owner/tenant record or recipient profile) so the
   * picker can group by client. Backed by the list_open_invoices_for_bundling
   * RPC, which company-scopes and excludes soft-deleted/non-open rows. Invoices
   * whose party can't be resolved are RETURNED with a NULL party (not hidden) so
   * the picker can surface them as "needs owner"; they are not bundleable.
   *
   * Staff op — flipped to Nest (`/invoices/open`, which forwards to the SAME
   * RPC server-side), Supabase fallback. Parsed through the shared
   * `openInvoiceRowSchema` (party fields + tolerant money). The RPC stays in PG.
   */
  async listOpen(companyId: string): Promise<OpenInvoiceRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.invoices.listOpen());
        return openInvoiceRowSchema.array().parse(rows) as unknown as OpenInvoiceRow[];
      } catch (err) {
        reportNestFallback("invoices.listOpen", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("list_open_invoices_for_bundling", { p_company_id: companyId });

    if (error) {
      console.error("Error fetching open invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as OpenInvoiceRow[];
  },

  /**
   * Per-invoice Qoyod sync state for the finance UI's marker.
   *
   * Nest-only by design: the state lives in the `app.accounting_*` tables, which
   * PostgREST does not expose. There is no Supabase fallback and there must not
   * be one — on any failure this returns EMPTY so the invoices table renders
   * exactly as before, minus the badges. A sync marker must never be able to
   * break the invoice list.
   */
  async listAccountingSyncState(): Promise<InvoiceSyncState[]> {
    if (!API_URL) return [];
    try {
      const rows = await nestRequest<unknown[]>(
        API_ROUTES.invoices.accountingSyncState(),
      );
      return invoiceSyncStateSchema.array().parse(rows);
    } catch (err) {
      reportNestFallback("invoices.listAccountingSyncState", err);
      return [];
    }
  },

  /**
   * List master invoices for the active company, newest first.
   * Pulls recipient profile + child invoices (via master_invoice_items),
   * filters out null child invoices, maps each to a MasterInvoiceChild, and
   * sorts the children by display_order. The `items` join column is stripped
   * from the master header before the mapped children are re-attached.
   * Company-scoped.
   */
  async listMasterInvoices(companyId: string): Promise<MasterInvoiceWithItems[]> {
    const { data, error } = await supabase
      .from("master_invoices")
      .select(
        `
        *,
        recipient:profiles!master_invoices_recipient_id_fkey(full_name, phone, email),
        items:master_invoice_items(
          display_order,
          invoice:invoices(id, invoice_number, description, subtotal, vat_rate, vat_amount, discount_amount, total_amount, balance, status, due_date, property:properties(name), unit:units(unit_number), contract:contracts(tenant:tenants(first_name, last_name, commercial_name)))
        )
      `,
      )
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading master invoices:", serializeError(error));
      throw error;
    }

    type RawItem = {
      display_order: number;
      invoice: {
        id: string;
        invoice_number: string | null;
        description: string | null;
        subtotal: number;
        vat_rate: number | null;
        vat_amount: number;
        discount_amount: number;
        total_amount: number;
        balance: number;
        status: Database["public"]["Enums"]["invoice_status"];
        due_date: string;
        property: { name: string | null } | null;
        unit: { unit_number: string | null } | null;
        contract: {
          tenant: {
            first_name: string | null;
            last_name: string | null;
            commercial_name: string | null;
          } | null;
        } | null;
      } | null;
    };

    // Tenant display name: prefer the commercial name (establishments), else the
    // person's first+last. Mirrors how the legacy invoice named the tenant.
    type RawTenant = {
      first_name: string | null;
      last_name: string | null;
      commercial_name: string | null;
    } | null;
    const tenantName = (t: RawTenant): string | null => {
      if (!t) return null;
      const commercial = t.commercial_name?.trim();
      if (commercial) return commercial;
      const full = [t.first_name?.trim(), t.last_name?.trim()].filter(Boolean).join(" ");
      return full || null;
    };

    return (data ?? []).map((row) => {
      const items = ((row.items ?? []) as RawItem[])
        .filter((it) => it.invoice !== null)
        .map((it) => ({
          id: it.invoice!.id,
          invoice_number: it.invoice!.invoice_number,
          description: it.invoice!.description,
          subtotal: it.invoice!.subtotal,
          vat_rate: it.invoice!.vat_rate,
          vat_amount: it.invoice!.vat_amount,
          discount_amount: it.invoice!.discount_amount,
          total_amount: it.invoice!.total_amount,
          balance: it.invoice!.balance,
          status: it.invoice!.status,
          due_date: it.invoice!.due_date,
          property_name: it.invoice!.property?.name ?? null,
          unit_number: it.invoice!.unit?.unit_number ?? null,
          tenant_name: tenantName(it.invoice!.contract?.tenant ?? null),
          display_order: it.display_order,
        }))
        .sort((a, b) => a.display_order - b.display_order);

      const { items: _omit, ...header } = row as typeof row & { items: RawItem[] };
      const h = header as Record<string, unknown>;
      return {
        ...(header as MasterInvoiceRow),
        recipient: row.recipient ?? null,
        client_party_kind: (h.client_party_kind as MasterInvoiceWithItems["client_party_kind"]) ?? null,
        client_party_id: (h.client_party_id as string | null) ?? null,
        client_name: (h.client_name as string | null) ?? null,
        items,
      };
    });
  },

  /**
   * Expense-invoice parity check: list paid invoices from a set of ids.
   * Used by useFinancialStats to count expense receipts.
   */
  async listPaidByIds(ids: string[], companyId: string): Promise<{ id: string }[]> {
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from("invoices")
      .select("id")
      .in("id", ids)
      .eq("company_id", companyId)
      .eq("status", "paid");

    if (error) {
      console.error("Error fetching paid invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { id: string }[];
  },

  /**
   * Find invoices linked to a set of scheduled_payment_ids.
   * Used by InvoicesTab to show pending-verification badges.
   */
  async listByScheduledPaymentIds(spIds: string[], companyId: string): Promise<InvoiceScheduledPaymentRow[]> {
    if (spIds.length === 0) return [];

    const { data, error } = await supabase
      .from("invoices")
      .select("id")
      .in("scheduled_payment_id", spIds)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching invoices by scheduled payment:", serializeError(error));
      throw error;
    }

    return (data ?? []) as InvoiceScheduledPaymentRow[];
  },

  // ─── invoice_notifications ────────────────────────────────────

  /**
   * List notification history for a single invoice, newest first.
   * Enriches sender full_name from profiles inline.
   *
   * Staff op — flipped to Nest (`/invoices/:id/notifications`, which replicates
   * the same sender enrichment server-side), Supabase fallback. Parsed through
   * the shared `invoiceNotificationRowSchema` (loose passthrough), cast back to
   * the Supabase-inferred row so consumers see no drift.
   */
  async listNotifications(invoiceId: string, companyId: string): Promise<InvoiceNotificationRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.invoices.notifications(invoiceId));
        return invoiceNotificationRowSchema.array().parse(rows) as unknown as InvoiceNotificationRow[];
      } catch (err) {
        reportNestFallback("invoices.listNotifications", err);
      }
    }
    const { data, error } = await supabase
      .from("invoice_notifications")
      .select("*")
      .eq("invoice_id", invoiceId)
      .eq("company_id", companyId)
      .order("sent_at", { ascending: false });

    if (error) {
      console.error("Error fetching invoice notifications:", serializeError(error));
      throw error;
    }

    const notifications = (data ?? []) as InvoiceNotificationRow[];

    // Resolve sender names from profiles
    const senderIds = [...new Set(notifications.map((n) => n.sent_by).filter(Boolean))] as string[];
    if (senderIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", senderIds);
      if (profiles) {
        const profileMap = new Map(profiles.map((p) => [p.id, p.full_name]));
        notifications.forEach((n) => {
          if (n.sent_by && profileMap.has(n.sent_by)) {
            n.sender = { full_name: profileMap.get(n.sent_by) ?? null };
          }
        });
      }
    }

    return notifications;
  },

  // ─── scheduled_payments ──────────────────────────────────────

  /**
   * List pending scheduled payments (verification_status='pending').
   * Used by InvoicesTab to build the pending-verification badge set.
   */
  async listPendingScheduledPayments(companyId: string): Promise<ScheduledPaymentPendingRow[]> {
    const { data, error } = await supabase
      .from("scheduled_payments")
      .select("id")
      .eq("company_id", companyId)
      .eq("verification_status", "pending");

    if (error) {
      console.error("Error fetching pending scheduled payments:", serializeError(error));
      throw error;
    }

    return (data ?? []) as ScheduledPaymentPendingRow[];
  },

  /**
   * `{ property_id, total_amount, paid_amount }` for every non-deleted
   * invoice attached to a property — the per-property collection summaries
   * the properties grid aggregates in-memory. Company-scoped; throws on
   * error.
   */
  async listPropertyCollectionRows(companyId: string): Promise<
    {
      property_id: string | null;
      total_amount: number | null;
      paid_amount: number | null;
    }[]
  > {
    const { data, error } = await supabase
      .from("invoices")
      .select("property_id, total_amount, paid_amount")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .not("property_id", "is", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * A property's invoices with unit/contract context, latest due first (the
   * property-drawer Invoices tab). Company-scoped; throws on error.
   */
  async listByProperty(companyId: string, propertyId: string) {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
          *,
          unit:units(unit_number, unit_type),
          contract:contracts(contract_number)
        `)
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .order("due_date", { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Per-unit invoice totals for a set of units (property-drawer Units tab
   * collection columns). Company-scoped. NEVER throws: the pre-consolidation
   * site destructured `{ data }` only (a failed read renders zero totals), so
   * an error resolves to [].
   */
  async listUnitTotalsByUnitIds(companyId: string, unitIds: string[]) {
    const { data } = await supabase
      .from("invoices")
      .select("unit_id, total_amount, paid_amount, balance")
      .eq("company_id", companyId)
      .in("unit_id", unitIds);
    return data ?? [];
  },

  /**
   * Payment columns for a tenant's contracts (tenant financial summary).
   * Company-scoped. NEVER throws: the pre-consolidation site destructured
   * `{ data }` only (summary renders zeros), so an error resolves to [].
   */
  async listFinancialSummaryByContracts(
    companyId: string,
    contractIds: string[],
  ): Promise<
    {
      total_amount: number | null;
      paid_amount: number | null;
      balance: number | null;
      status: string;
      due_date: string;
    }[]
  > {
    const { data } = await supabase
      .from("invoices")
      .select("total_amount, paid_amount, balance, status, due_date")
      .eq("company_id", companyId)
      .in("contract_id", contractIds);
    return data ?? [];
  },

  /**
   * A contract's full non-deleted invoice rows, due-date ascending — the
   * contract-details Payment Schedule table. The soft-delete filter matters:
   * the `soft_delete_invoices` trigger turns schedule-rebuild DELETEs into
   * soft-deletes, and unfiltered ghosts would reappear here. Company-scoped;
   * throws on error.
   */
  async listByContractForSchedule(contractId: string, companyId: string) {
    const { data, error } = await supabase
      .from("invoices")
      .select("*")
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("due_date", { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /**
   * A tenant's invoice history (with property/unit/contract context) across
   * their contracts, latest due first. Company-scoped; throws on error. KEPT
   * on Supabase.
   */
  async listHistoryByContracts(companyId: string, contractIds: string[]) {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
          *,
          property:properties(name),
          unit:units(unit_number),
          contract:contracts(contract_number)
        `)
      .eq("company_id", companyId)
      .in("contract_id", contractIds)
      .order("due_date", { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * HEAD-only count of pending transfer verifications — the badge on the
   * Finance page's "Transfer Verifications" button. Company-scoped; throws on
   * error (mirrors the pre-consolidation site).
   */
  async countPendingVerifications(companyId: string): Promise<number> {
    const { count, error } = await supabase
      .from("scheduled_payments")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .eq("verification_status", "pending");
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * Every scheduled payment that entered the verification flow
   * (verification_status non-null), newest proof first, joined with contract →
   * tenant/unit/property display columns. Company-scoped; throws on error.
   * The page splits rows into pending/approved/rejected tabs in-memory.
   */
  async listVerificationProofs(companyId: string): Promise<ScheduledPaymentProofRow[]> {
    const { data, error } = await supabase
      .from("scheduled_payments")
      .select(`
          id, contract_id, company_id, payment_number, due_date, amount, paid_amount, status,
          transfer_proof_url, transfer_method, transfer_reference_number,
          transfer_proof_uploaded_at, verification_status, verified_at, rejection_reason,
          contract:contracts!inner(
            contract_number,
            tenant:tenants(first_name, last_name, first_name_en, last_name_en, commercial_name, commercial_name_en),
            unit:units(unit_number),
            property:properties(name)
          )
        `)
      .eq("company_id", companyId)
      .not("verification_status", "is", null)
      .order("transfer_proof_uploaded_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as ScheduledPaymentProofRow[];
  },

  /**
   * Invoices explicitly linked to a scheduled payment (the preferred match for
   * the approve dialog's amount preview). NOT company-filtered — RLS scopes it
   * (verbatim from the pre-consolidation site). NEVER throws: the site ignored
   * errors (a failed lookup just skips the amount preview; the verify RPC
   * recomputes server-side), so an error resolves to [].
   */
  async listVerificationMatchByScheduledPayment(
    scheduledPaymentId: string,
  ): Promise<InvoiceVerificationMatchRow[]> {
    const { data } = await supabase
      .from("invoices")
      .select("id, total_amount, paid_amount, subtotal, vat_amount, status, due_date, created_at")
      .eq("scheduled_payment_id", scheduledPaymentId);
    return (data ?? []) as InvoiceVerificationMatchRow[];
  },

  /**
   * All of a contract's invoices, for the approve dialog's fallback match
   * (earliest unpaid). Same NOT-company-filtered / NEVER-throws contract as
   * `listVerificationMatchByScheduledPayment`.
   */
  async listVerificationMatchByContract(
    contractId: string,
  ): Promise<InvoiceVerificationMatchRow[]> {
    const { data } = await supabase
      .from("invoices")
      .select("id, total_amount, paid_amount, subtotal, vat_amount, status, due_date, created_at")
      .eq("contract_id", contractId);
    return (data ?? []) as InvoiceVerificationMatchRow[];
  },

  // ─── Atomic RPCs (kept in Postgres) ──────────────────────────

  /**
   * Create a master (bundled) invoice via the `create_master_invoice` RPC.
   * Atomic: generates master invoice number + links child invoices.
   * Kept in Postgres — do NOT decompose into JS.
   *
   * Staff op — flipped to Nest (`POST /invoices/master`, which forwards to the
   * SAME RPC server-side; company_id is injected from the JWT, never the body),
   * Supabase fallback. The RPC result shape varies, so it is passed through
   * unparsed (the caller refetches the master-invoice list afterwards).
   */
  async createMasterInvoice({ companyId, invoiceIds, recipientId, invoiceType, notes }: CreateMasterInvoiceParams) {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(API_ROUTES.invoices.masterCreate(), {
          method: "POST",
          body: JSON.stringify({
            invoiceIds,
            recipientId: recipientId ?? undefined,
            invoiceType: invoiceType ?? undefined,
            notes: notes ?? undefined,
          }),
        });
      } catch (err) {
        // Write guard: `create_master_invoice`'s "already in a master invoice"
        // check makes a post-commit retry FAIL loudly rather than duplicate —
        // the fallback would report the succeeded bundle as an error. Only a
        // known-uncommitted 4xx may retry through Supabase.
        reportNestFallbackOrRethrowUnknownCommit("invoices.createMasterInvoice", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("create_master_invoice", {
      p_company_id: companyId,
      p_invoice_ids: invoiceIds,
      p_recipient_id: recipientId ?? undefined,
      p_invoice_type: invoiceType ?? undefined,
      p_notes: notes ?? undefined,
    });

    if (error) {
      console.error("Error creating master invoice:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Settle a master invoice via the `pay_master_invoice` RPC (manual /
   * bank-transfer path). Atomic: fans out one receipt per child invoice and
   * marks each child + the master paid. Idempotent server-side. Kept in
   * Postgres — do NOT decompose. Defaults the payment channel to "card".
   *
   * Flipped to Nest (`POST /invoices/master/:id/pay`, which forwards to the SAME
   * RPC through the caller's token so its internal `auth.uid()` tenancy guard
   * fires), Supabase fallback. This is the CLIENT path — distinct from the
   * HyperPay card-webhook settle (server-side, service-role).
   */
  async payMasterInvoice(
    { masterInvoiceId, paymentChannel, referenceNumber }: PayMasterInvoiceParams,
  ): Promise<{ receipts_created: number; idempotent: boolean; status: string }> {
    if (API_URL) {
      try {
        return await nestRequest<{ receipts_created: number; idempotent: boolean; status: string }>(
          API_ROUTES.invoices.masterPay(masterInvoiceId),
          {
            method: "POST",
            body: JSON.stringify({
              paymentChannel: paymentChannel ?? undefined,
              referenceNumber: referenceNumber ?? undefined,
            }),
          },
        );
      } catch (err) {
        // Reviewed 2026-07-18 (write-guard sweep): read-path fallback is SAFE
        // here. `pay_master_invoice` is idempotent server-side (status='paid'
        // → no-op returning idempotent:true), so retrying the same RPC after
        // an ambiguous Nest failure cannot double-settle.
        reportNestFallback("invoices.payMasterInvoice", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("pay_master_invoice", {
      p_master_invoice_id: masterInvoiceId,
      p_payment_channel: paymentChannel ?? "card",
      p_reference_number: referenceNumber ?? undefined,
    });

    if (error) {
      console.error("Error paying master invoice:", serializeError(error));
      throw error;
    }

    return data as { receipts_created: number; idempotent: boolean; status: string };
  },

  /**
   * Cancel a DUE master invoice and release its children (the bundle links are
   * removed so each child invoice returns to standalone open/payable). PAID
   * masters cannot be cancelled; already-cancelled is a no-op. Enforced in the
   * `cancel_master_invoice` RPC.
   *
   * Flipped to Nest (`POST /invoices/master/:id/cancel`), Supabase fallback.
   */
  async cancelMasterInvoice(masterInvoiceId: string): Promise<CancelMasterInvoiceResult> {
    if (API_URL) {
      try {
        return await nestRequest<CancelMasterInvoiceResult>(
          API_ROUTES.invoices.masterCancel(masterInvoiceId),
          { method: "POST" },
        );
      } catch (err) {
        // Reviewed 2026-07-18 (write-guard sweep): read-path fallback is SAFE
        // here. `cancel_master_invoice` is idempotent server-side (already
        // cancelled → no-op returning idempotent:true), so a post-commit retry
        // cannot double-cancel or corrupt state.
        reportNestFallback("invoices.cancelMasterInvoice", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("cancel_master_invoice", {
      p_master_invoice_id: masterInvoiceId,
    });

    if (error) {
      console.error("Error cancelling master invoice:", serializeError(error));
      throw error;
    }

    return data as CancelMasterInvoiceResult;
  },

  /**
   * Hard-delete a CANCELLED master invoice (CEO request — "delete cancelled
   * master invoices"). Unlike cancel/pay (idempotent no-ops, safe under the
   * read-path `reportNestFallback`), a hard DELETE is NOT idempotent: if the
   * Nest call actually committed the delete but the response was lost
   * (network drop, timeout, 5xx), re-issuing through the Supabase fallback
   * would hit "not found" instead of quietly no-oping — an ambiguous-commit
   * write, same class as `createMasterInvoice`. Uses the write-safe
   * `reportNestFallbackOrRethrowUnknownCommit` (duplicate-write guard).
   *
   * Flipped to Nest (`DELETE /invoices/master/:id`), Supabase fallback.
   */
  async deleteMasterInvoice(masterInvoiceId: string): Promise<DeleteMasterInvoiceResult> {
    if (API_URL) {
      try {
        return await nestRequest<DeleteMasterInvoiceResult>(
          API_ROUTES.invoices.masterDelete(masterInvoiceId),
          { method: "DELETE" },
        );
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("invoices.deleteMasterInvoice", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("delete_master_invoice", {
      p_master_invoice_id: masterInvoiceId,
    });

    if (error) {
      console.error("Error deleting master invoice:", serializeError(error));
      throw error;
    }

    return data as DeleteMasterInvoiceResult;
  },

  /**
   * Approve or reject a tenant's bank-transfer payment proof via the
   * `verify_payment_proof` RPC. On approval the RPC atomically records the
   * receipt (bank_transfer channel) and marks the scheduled payment paid/
   * partial; on rejection it stamps the reason. `paidAmount` defaults to null so
   * the RPC derives the linked invoice's full outstanding balance server-side
   * (Fortress rule 1). Kept in Postgres — do NOT decompose.
   *
   * Flipped to Nest (`POST /invoices/payment-proofs/:id/verify`, forwarded via
   * the caller's token so the RPC's internal `finance.edit` + tenancy check on
   * `auth.uid()` fires), Supabase fallback.
   */
  async verifyPaymentProof(
    { paymentId, action, paidAmount, reason }: VerifyPaymentProofParams,
  ): Promise<unknown> {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(
          API_ROUTES.invoices.verifyPaymentProof(paymentId),
          {
            method: "POST",
            body: JSON.stringify({
              action,
              paidAmount: paidAmount ?? null,
              reason: reason ?? null,
            }),
          },
        );
      } catch (err) {
        // Write guard: `verify_payment_proof` rejects a post-commit retry with
        // "Payment is not pending verification" — the fallback would surface
        // an approval that already recorded its receipt as a hard failure.
        // Only a known-uncommitted 4xx may retry through Supabase.
        reportNestFallbackOrRethrowUnknownCommit("invoices.verifyPaymentProof", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("verify_payment_proof", {
      p_payment_id: paymentId,
      p_action: action,
      p_paid_amount: paidAmount ?? null,
      p_reason: reason ?? null,
    });

    if (error) {
      console.error("Error verifying payment proof:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Tenant submits a bank-transfer proof for a scheduled payment via the
   * `submit_payment_proof` RPC — writes the proof fields, flips
   * `verification_status` to pending, and notifies company admins. The RPC's
   * internal caller-is-the-tenant-on-the-contract check authorizes (the
   * Storage upload itself stays client-side until the Storage exit). Kept in
   * Postgres — do NOT decompose.
   *
   * Flipped to Nest (`POST /finance/payment-proofs/:id/submit`, auth-only —
   * forwarded with the caller's identity so the RPC's tenant check fires),
   * Supabase fallback.
   */
  async submitPaymentProof(
    { paymentId, proofUrl, method, referenceNumber }: SubmitPaymentProofParams,
  ): Promise<unknown> {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(
          API_ROUTES.invoices.submitPaymentProof(paymentId),
          {
            method: "POST",
            body: JSON.stringify({
              proofUrl,
              method: method ?? "bank_transfer",
              referenceNumber: referenceNumber ?? null,
            }),
          },
        );
      } catch (err) {
        // Write guard: a replayed submit after a COMMITTED Nest write would
        // re-stamp transfer_proof_uploaded_at, re-fan-out the admin
        // notifications — and, if a verify raced in between, RESET an
        // approved/rejected payment back to pending (a real state
        // regression). Only a known-uncommitted 4xx may retry through
        // Supabase.
        reportNestFallbackOrRethrowUnknownCommit("invoices.submitPaymentProof", err);
      }
    }
    const { data, error } = await (supabase as unknown as {
      rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
    }).rpc("submit_payment_proof", {
      p_payment_id: paymentId,
      p_proof_url: proofUrl,
      p_method: method ?? "bank_transfer",
      p_reference_number: referenceNumber ?? null,
    });

    if (error) {
      console.error("Error submitting payment proof:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * List tenant_id / balance / status for outstanding invoices.
   * Used by TenantsReport to compute per-tenant outstanding balance.
   */
  async listOutstandingBalances(companyId: string): Promise<{ tenant_id: string | null; balance: number | null; status: string | null }[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("tenant_id, balance, status")
      .eq("company_id", companyId)
      .in("status", ["due", "overdue", "partial"]);

    if (error) {
      console.error("Error fetching outstanding balances:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { tenant_id: string | null; balance: number | null; status: string | null }[];
  },

  /**
   * List tenant_id values from overdue invoices (for counting unique late payers).
   * Used by CollectionsReport.
   */
  async listOverdueTenantIds(companyId: string): Promise<(string | null)[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select("tenant_id")
      .eq("company_id", companyId)
      .eq("status", "overdue");

    if (error) {
      console.error("Error fetching overdue tenant ids:", serializeError(error));
      throw error;
    }

    return (data ?? []).map((r) => (r as { tenant_id: string | null }).tenant_id);
  },

  // ─── Dashboard widgets ────────────────────────────────────────

  /**
   * Upcoming (overdue/issued) invoices for the dashboard widget.
   * Ordered by due_date ascending, limited to the `limit` most urgent.
   */
  async listUpcoming(companyId: string, todayStr: string, limit = 4) {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id,
        invoice_number,
        due_date,
        total_amount,
        contract:contracts(
          tenant:tenants(first_name, last_name),
          unit:units(unit_number, property:properties(name))
        )
      `)
      .eq("company_id", companyId)
      .in("status", ["issued", "overdue"])
      .gte("due_date", todayStr)
      .order("due_date", { ascending: true })
      .limit(limit);

    if (error) {
      console.error("Error fetching upcoming payments:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      invoice_number: string | null;
      due_date: string;
      total_amount: number | null;
      contract: {
        tenant: { first_name: string | null; last_name: string | null } | null;
        unit: { unit_number: string | null; property: { name: string } | null } | null;
      } | null;
    }>;
  },

  // ─── Additional report projections ───────────────────────────

  /**
   * Accounts receivable report projection — unpaid invoices with
   * recipient and contract relations.
   */
  async listForAccountsReceivable(companyId: string): Promise<Array<{
    id: string;
    total_amount: number | null;
    balance: number | null;
    status: string | null;
    due_date: string;
    description: string | null;
    recipient: { full_name: string | null } | null;
    contract: { contract_type: string | null } | null;
  }>> {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id, total_amount, balance, status, due_date, description,
        recipient:profiles!invoices_recipient_id_fkey(full_name),
        contract:contracts(contract_type)
      `)
      .eq("company_id", companyId)
      .in("status", ["due", "overdue", "partial"])
      .order("due_date", { ascending: true });

    if (error) {
      console.error("Error fetching AR invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      total_amount: number | null;
      balance: number | null;
      status: string | null;
      due_date: string;
      description: string | null;
      recipient: { full_name: string | null } | null;
      contract: { contract_type: string | null } | null;
    }>;
  },

  /**
   * Invoice report projection — invoices with recipient name.
   */
  async listForInvoiceReport(companyId: string): Promise<Array<{
    id: string;
    invoice_number: string | null;
    total_amount: number | null;
    status: string | null;
    invoice_date: string | null;
    due_date: string;
    recipient: { full_name: string | null } | null;
  }>> {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id, invoice_number, total_amount, status, invoice_date, due_date,
        recipient:profiles!invoices_recipient_id_fkey(full_name)
      `)
      .eq("company_id", companyId)
      .order("invoice_date", { ascending: false });

    if (error) {
      console.error("Error fetching invoice report:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      invoice_number: string | null;
      total_amount: number | null;
      status: string | null;
      invoice_date: string | null;
      due_date: string;
      recipient: { full_name: string | null } | null;
    }>;
  },

  /**
   * Revenue report projection — invoices with amounts, dates, property and unit.
   */
  async listForRevenueReport(companyId: string): Promise<Array<{
    id: string;
    invoice_number: string | null;
    total_amount: number | null;
    paid_amount: number | null;
    balance: number | null;
    status: string | null;
    due_date: string;
    invoice_date: string | null;
    property: { name: string } | null;
    unit: { unit_number: string | null } | null;
  }>> {
    const { data, error } = await supabase
      .from("invoices")
      .select(`
        id, invoice_number, total_amount, paid_amount, balance, status, due_date, invoice_date,
        property:properties(name),
        unit:units(unit_number)
      `)
      .eq("company_id", companyId)
      .order("due_date", { ascending: false });

    if (error) {
      console.error("Error fetching revenue report invoices:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      invoice_number: string | null;
      total_amount: number | null;
      paid_amount: number | null;
      balance: number | null;
      status: string | null;
      due_date: string;
      invoice_date: string | null;
      property: { name: string } | null;
      unit: { unit_number: string | null } | null;
    }>;
  },

  // ─── Super-admin cross-company reads ──────────────────────────────
  // The super-admin Invoices page reads operational invoices across ALL
  // companies. super_admin's get_user_company_ids() spans every company, so the
  // existing `invoices` SELECT policy already permits the cross-company read
  // (same path the super-admin Dashboard uses). The KPI SUM is Nest-first since
  // c1 (`GET /invoices/admin/sum-by-status`, SuperAdminGuard, parity-pinned);
  // the remaining reads (count, list, company tabs) are still DEFERRED from the
  // Nest flip and stay on Supabase. Mirrors the service_requests super-admin
  // company-tabs pattern.

  /**
   * Super-admin platform-wide invoice COUNT for one status (NOT company-scoped),
   * optionally narrowed by `status`. HEAD-only count query — zero rows
   * transferred. Companion to `sumTotalByStatusForSuperAdmin`; part of the
   * documented super-admin cross-company carve-out (relies on the super_admin RLS
   * span, the same path the dashboard already used for these reads).
   */
  async countByStatusForSuperAdmin(status?: string): Promise<number> {
    let query = supabase
      .from("invoices")
      .select("*", { count: "exact", head: true })
      .is("deleted_at", null);

    if (status) query = query.eq("status", status as Database["public"]["Enums"]["invoice_status"]);

    const { count, error } = await query;
    if (error) {
      console.error("Error counting invoices by status:", serializeError(error));
      throw error;
    }
    return count ?? 0;
  },

  /**
   * Super-admin platform-wide SUM of invoice `total_amount` for one status (NOT
   * company-scoped). Nest-first since c1: `GET /invoices/admin/sum-by-status`
   * (SuperAdminGuard, halala-parity pinned against the RPC). Degradation chain
   * kept verbatim below: Nest error → the guarded SECURITY DEFINER aggregate
   * `sum_invoices_total_by_status` (zero rows transferred) → row-fetch +
   * client sum (deploy-order safe; the 42501/PGRST202 warn-not-error logic
   * keeps expected auth-transition noise out of Sentry). super-admin only (the
   * guard and the RPC each re-assert the boundary).
   */
  async sumTotalByStatusForSuperAdmin(status: string): Promise<number> {
    if (API_URL) {
      try {
        const res = await nestRequest<{ status?: string; total?: number }>(
          API_ROUTES.invoices.adminSumByStatus(status),
        );
        return Number(res?.total ?? 0);
      } catch (err) {
        reportNestFallback("invoices.sumTotalByStatusForSuperAdmin", err);
      }
    }
    let rpcError: { message?: string; code?: string } | null = null;
    try {
      const { data, error } = await (supabase as unknown as {
        rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string; code?: string } | null }>;
      }).rpc("sum_invoices_total_by_status", { p_status: status });
      if (!error) return Number(data ?? 0);
      rpcError = error;
    } catch (err) {
      rpcError = (err ?? {}) as { message?: string; code?: string };
    }

    // RPC errored — fall back to the row-fetch + client sum so the dashboard
    // still renders the amount (the pre-RPC behaviour).
    //
    // Two of these "failures" are EXPECTED and self-healing, so they log at WARN
    // (captureConsoleIntegration only forwards console.error to Sentry, so warn
    // stays out of the issue stream):
    //   - 42501 "permission denied for function" — the caller is `anon`. The RPC
    //     grants EXECUTE to `authenticated` and revokes it from `anon`, so an
    //     authenticated super-admin never sees this; it only fires when an
    //     in-flight dashboard refetch runs during an auth transition (e.g. on
    //     /auth after a session expired). The next authenticated load succeeds.
    //   - PGRST202 — the function isn't in this DB's schema cache yet (the
    //     deploy-order window before the migration applies).
    // Anything else is unexpected and stays at ERROR level so real bugs surface.
    const benign = rpcError?.code === "42501" || rpcError?.code === "PGRST202";
    (benign ? console.warn : console.error)(
      "sum_invoices_total_by_status RPC failed, falling back to row sum:",
      serializeError(rpcError),
    );

    const { data, error } = await supabase
      .from("invoices")
      .select("total_amount")
      .eq("status", status as Database["public"]["Enums"]["invoice_status"])
      .is("deleted_at", null);
    if (error) {
      console.error("Error summing invoices by status:", serializeError(error));
      throw error;
    }
    return (data ?? []).reduce(
      (sum, row) => sum + ((row as { total_amount: number | null }).total_amount ?? 0),
      0,
    );
  },

  /**
   * Companies that have invoices — drives the super-admin Invoices company tab
   * strip (main company first, always present via get_main_company_id). Mirrors
   * `serviceRequestsRepository.companiesWithRequests`. `total` is the invoice
   * count per company. Soft-deleted invoices excluded.
   */
  async companiesWithInvoices(
    { companyId, scopeToCompany = true }: ScopeOptions,
  ): Promise<InvoiceCompany[]> {
    let countQuery = supabase
      .from("invoices")
      .select("company_id")
      .is("deleted_at", null);
    if (scopeToCompany) countQuery = countQuery.eq("company_id", companyId);
    const { data: rows, error } = await countQuery;
    if (error) {
      console.error("Error fetching invoice companies:", serializeError(error));
      throw error;
    }

    const totals = new Map<string, number>();
    for (const r of (rows ?? []) as { company_id: string | null }[]) {
      if (!r.company_id) continue; // orphan rows (null company) can't be a tab
      totals.set(r.company_id, (totals.get(r.company_id) ?? 0) + 1);
    }

    let mainCompanyId: string | null = null;
    if (!scopeToCompany) {
      // Route through the companies repository chokepoint (slice a14 / N-3) — no
      // inline supabase.rpc("get_main_company_id"). Best-effort: a failed main
      // lookup just omits the main-company tab; it never breaks the stats read.
      try {
        mainCompanyId = await companiesRepository.getMainCompanyId();
      } catch {
        mainCompanyId = null;
      }
      if (mainCompanyId && !totals.has(mainCompanyId)) totals.set(mainCompanyId, 0);
    }

    const ids = [...totals.keys()];
    if (ids.length === 0) return [];

    const { data: companies } = await supabase
      .from("companies")
      .select("id, name, name_en")
      .in("id", ids);
    const nameMap = new Map(
      ((companies ?? []) as { id: string; name: string; name_en: string | null }[]).map(
        (c) => [c.id, c],
      ),
    );

    const result: InvoiceCompany[] = ids.map((id) => {
      const c = nameMap.get(id);
      return {
        company_id: id,
        name: c?.name ?? "",
        name_en: c?.name_en ?? null,
        total: totals.get(id) ?? 0,
        is_main: id === mainCompanyId,
      };
    });
    result.sort(
      (a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0) || b.total - a.total,
    );
    return result;
  },

  /**
   * Cross-company invoice list for the super-admin Invoices page. When a company
   * tab is selected (selectedCompanyId), narrows to it; otherwise returns every
   * company's invoices the RLS allows. Newest first. Soft-deleted excluded.
   * Company-scoped roles always pass scopeToCompany=true (own company only).
   */
  async listForSuperAdmin(
    { companyId, scopeToCompany = true, selectedCompanyId }:
      ScopeOptions & { selectedCompanyId?: string | null },
  ): Promise<SuperAdminInvoiceRow[]> {
    let query = supabase
      .from("invoices")
      .select(`
        id, invoice_number, company_id, total_amount, balance, paid_amount,
        status, invoice_date, due_date, description,
        recipient:profiles!invoices_recipient_id_fkey(full_name),
        contract:contracts(
          tenant:tenants(first_name, last_name, commercial_name),
          owner:owners(full_name)
        ),
        property:properties(name),
        unit:units(unit_number)
      `)
      .is("deleted_at", null);

    if (scopeToCompany) query = query.eq("company_id", companyId);
    else if (selectedCompanyId) query = query.eq("company_id", selectedCompanyId);

    const { data, error } = await query
      .order("invoice_date", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching super-admin invoices:", serializeError(error));
      throw error;
    }

    type RawRow = {
      id: string;
      invoice_number: string | null;
      company_id: string;
      total_amount: number | null;
      balance: number | null;
      paid_amount: number | null;
      status: string | null;
      invoice_date: string | null;
      due_date: string;
      description: string | null;
      recipient: { full_name: string | null } | null;
      contract: {
        tenant: { first_name: string | null; last_name: string | null; commercial_name: string | null } | null;
        owner: { full_name: string | null } | null;
      } | null;
      property: { name: string } | null;
      unit: { unit_number: string | null } | null;
    };

    return ((data ?? []) as unknown as RawRow[]).map((r) => {
      const tenant = r.contract?.tenant;
      const tenantName =
        tenant?.commercial_name ||
        [tenant?.first_name, tenant?.last_name].filter(Boolean).join(" ").trim() ||
        null;
      const clientName =
        r.recipient?.full_name || tenantName || r.contract?.owner?.full_name || null;
      return {
        id: r.id,
        invoice_number: r.invoice_number,
        company_id: r.company_id,
        total_amount: r.total_amount,
        balance: r.balance,
        paid_amount: r.paid_amount,
        status: r.status,
        invoice_date: r.invoice_date,
        due_date: r.due_date,
        description: r.description,
        client_name: clientName,
        property_name: r.property?.name ?? null,
        unit_number: r.unit?.unit_number ?? null,
      };
    });
  },
};
